import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { CreditLimits } from './components/credit-limits';
import { FieldMappings } from './components/field-mappings';
import { General } from './components/general';
import { Integrations } from './components/integrations';
import { Permissions, Roles } from './components/roles';
import { Templates } from './components/templates';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { SettingsNav, SettingsNavGroup, SettingsNavItem, SettingsWrapper } from '@/new/components/settings-nav';
import { SettingsBackLink } from '@/new/components/settings-section';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

enum ConfigurationTab {
  General = 'general',
  Templates = 'templates',
  Credits = 'credits',
  Integrations = 'integrations',
  Roles = 'roles',
}

interface OrgTab {
  id: string;
  permission: Permission;
  title: string;
  icon: React.ReactNode;
  uiMode?: 'hidden' | 'disabled';
  disabledMessage?: string;
}

// Labels for breadcrumb segments that aren't top-level tabs (drilled-into sub-pages).
const SUB_PAGE_LABELS: Record<string, string> = {
  'field-mappings': 'Relativity Field Mappings',
  permissions: 'Permissions Configuration',
};

const tabs: OrgTab[] = [
  {
    id: ConfigurationTab.General,
    permission: Permission.OrganisationManage,
    title: 'General',
    icon: <Icon className="lucide--settings" />,
  },
  {
    id: ConfigurationTab.Templates,
    permission: Permission.PromptTemplateManage,
    title: 'Templates',
    icon: <Icon className="lucide--layout-template" />,
  },
  {
    id: ConfigurationTab.Credits,
    permission: Permission.TokenLimitsManage,
    title: 'Credit Limits',
    icon: <Icon className="lucide--ban" />,
  },
  {
    id: ConfigurationTab.Integrations,
    permission: Permission.IntegrationsManage,
    title: 'Integrations',
    icon: <Icon className="lucide--unplug" />,
  },
  {
    id: ConfigurationTab.Roles,
    permission: Permission.UserManage,
    title: 'Roles & Permissions',
    icon: <Icon className="lucide--shield-user" />,
  },
];

function PermissionTabTrigger({ tab, isActive }: { tab: OrgTab; isActive: boolean }) {
  const { hasPermission } = useHasPermission();
  const allowed = hasPermission(tab.permission);
  const uiMode = tab.uiMode ?? 'hidden';

  if (!allowed && uiMode === 'hidden') return null;

  if (!allowed && uiMode === 'disabled') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <SettingsNavItem disabled>
              {tab.icon}
              <span>{tab.title}</span>
            </SettingsNavItem>
          </div>
        </TooltipTrigger>
        <TooltipContent>{tab.disabledMessage ?? 'You do not have permission to access this'}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <SettingsNavItem asChild active={isActive}>
      <Link to={tab.id}>
        {tab.icon}
        <span>{tab.title}</span>
      </Link>
    </SettingsNavItem>
  );
}

/**
 * Settings sub-page drilled into from the Integrations tab. The parent nav stays
 * visible alongside it.
 */
function FieldMappingsSettings() {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SettingsBackLink to="/organisation-settings/integrations" label="Back to integrations" />
      <div className="mb-6 px-1">
        <h2 className="text-xl font-medium text-gray-800">Field Mappings</h2>
        <p className="mt-1 text-sm text-gray-500">Import file metadata from your Relativity workspaces.</p>
      </div>
      <FieldMappings />
    </div>
  );
}

function PermissionsSettings() {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SettingsBackLink to="/organisation-settings/roles" label="Back to roles and permissions" />
      <Permissions />
    </div>
  );
}

function SettingsRedirect() {
  const { hasPermission } = useHasPermission();

  const firstAllowedTab = tabs.find((tab) => hasPermission(tab.permission));

  if (firstAllowedTab) {
    return <Navigate to={firstAllowedTab.id} replace />;
  }

  return <Navigate to="/matters" replace />;
}

export function OrganisationSettings() {
  const { t } = useTranslation();
  const location = useLocation();

  const settingsPathSegments = useMemo(
    () => location.pathname.split('/').filter(Boolean).slice(1),
    [location.pathname],
  );
  const activeTab = settingsPathSegments[0] as ConfigurationTab | undefined;

  // Sub-pages (e.g. field mappings) drill in from a tab. The nav stays visible
  // and the parent tab stays highlighted; only the content layout changes so the
  // configurator can fill the height.
  const isSubPage = settingsPathSegments.length > 1;

  // Build the breadcrumb trail from the URL path: Org Settings / <tab> / <sub-page>.
  // Tab labels reuse the canonical titles in `tabs` so they stay in sync.
  const breadcrumbItems = useMemo(() => {
    const settingsLabel = `${capitalizeFirstLetter(t('organisation'))} Settings`;
    const tabLabels = Object.fromEntries(tabs.map((tab) => [tab.id, tab.title]));

    const segments = settingsPathSegments;

    const items = [{ href: '/organisation-settings', label: settingsLabel, active: segments.length === 0 }];

    let href = '/organisation-settings';
    segments.forEach((segment, index) => {
      href += `/${segment}`;
      items.push({
        href,
        label: tabLabels[segment] ?? SUB_PAGE_LABELS[segment] ?? capitalizeFirstLetter(segment),
        active: index === segments.length - 1,
      });
    });

    return items;
  }, [settingsPathSegments, t]);

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={breadcrumbItems} />

      <SettingsWrapper>
        <SettingsNav>
          <SettingsNavGroup>
            {tabs.map((tab) => (
              <PermissionTabTrigger key={tab.id} tab={tab} isActive={activeTab === tab.id} />
            ))}
          </SettingsNavGroup>
        </SettingsNav>

        <div className={`min-h-0 flex-1 px-8 pt-7 pb-8 ${isSubPage ? 'flex flex-col' : 'overflow-auto'}`}>
          <Routes>
            <Route index element={<SettingsRedirect />} />
            <Route path="general" element={<General />} />
            <Route path="templates" element={<Templates />} />
            <Route path="credits" element={<CreditLimits />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="integrations/field-mappings" element={<FieldMappingsSettings />} />
            <Route path="roles" element={<Roles />} />
            <Route path="roles/permissions" element={<PermissionsSettings />} />
          </Routes>
        </div>
      </SettingsWrapper>
    </div>
  );
}
